import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FiBarChart2, 
  FiUsers, 
  FiMonitor,
  FiSmartphone,
  FiRefreshCw,
  FiTrendingUp,
  FiGlobe,
  FiActivity
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  font-weight: ${props => props.active ? '600' : '400'};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  bottom: -2px;

  &:hover {
    color: #3b82f6;
  }
`;

const Title = styled.h1`
  color: #111827;
  font-size: 2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DateFilter = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Select = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const StatIcon = styled.div`
  width: 3rem;
  height: 3rem;
  background: ${props => props.color || '#3b82f6'}20;
  color: ${props => props.color || '#3b82f6'};
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  color: #111827;
  font-size: 2rem;
  font-weight: 700;
`;

const StatChange = styled.div`
  color: ${props => props.positive ? '#10b981' : '#ef4444'};
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ChartSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
`;

const ChartTitle = styled.h3`
  color: #111827;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const RealtimeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #10b981;
  color: white;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  margin-left: auto;
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #ef4444;
`;

const Analytics = () => {
  const [activeTab, setActiveTab] = useState('visitors'); // 'visitors' or 'clicks'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7days');
  const [realtimeCount, setRealtimeCount] = useState(0);
  
  // Visitor Analytics State
  const [stats, setStats] = useState({
    totalVisits: 0,
    uniqueVisitors: 0,
    avgVisitsPerUser: 0
  });
  const [visitsByPage, setVisitsByPage] = useState([]);
  const [visitsByDevice, setVisitsByDevice] = useState([]);
  const [visitsByBrowser, setVisitsByBrowser] = useState([]);
  const [visitsByReferrer, setVisitsByReferrer] = useState([]);
  const [visitsOverTime, setVisitsOverTime] = useState([]);
  const [landingPages, setLandingPages] = useState([]);

  // Click Analytics State
  const [clickStats, setClickStats] = useState({
    totalClicks: 0,
    uniqueSessions: 0,
    avgClicksPerSession: 0
  });
  const [clicksByElement, setClicksByElement] = useState([]);
  const [clicksByCategory, setClicksByCategory] = useState([]);
  const [clicksByPage, setClicksByPage] = useState([]);
  const [clicksOverTime, setClicksOverTime] = useState([]);

  const API_URL = 'https://fazyra.com';

  useEffect(() => {
    if (activeTab === 'visitors') {
      fetchAnalytics();
      fetchRealtimeVisitors();
      
      // Refresh realtime count every 30 seconds
      const interval = setInterval(fetchRealtimeVisitors, 30000);
      return () => clearInterval(interval);
    } else if (activeTab === 'clicks') {
      fetchClickAnalytics();
    }
  }, [dateRange, activeTab]);

  const getDateRange = (range) => {
    const end = new Date();
    const start = new Date();
    
    switch (range) {
      case '24hours':
        start.setHours(start.getHours() - 24);
        break;
      case '7days':
        start.setDate(start.getDate() - 7);
        break;
      case '30days':
        start.setDate(start.getDate() - 30);
        break;
      case '90days':
        start.setDate(start.getDate() - 90);
        break;
      default:
        start.setDate(start.getDate() - 7);
    }
    
    return {
      start: start.toISOString(),
      end: end.toISOString()
    };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Try both token keys (adminToken is the correct one)
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      
      // Check if token exists
      if (!token) {
        throw new Error('Not authenticated. Please login first.');
      }
      
      const dates = getDateRange(dateRange);
      
      const response = await fetch(
        `${API_URL}/api/analytics/visitors/summary?startDate=${dates.start}&endDate=${dates.end}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(`Failed to fetch analytics (${response.status})`);
      }

      const result = await response.json();

      if (result.success) {
        const data = result.data;
        
        setStats({
          totalVisits: data.totalVisits,
          uniqueVisitors: data.uniqueVisitors,
          avgVisitsPerUser: data.avgVisitsPerUser
        });

        // Format data for charts
        setVisitsByPage(
          data.visitsByPage.map(item => ({
            page: item._id || 'Unknown',
            visits: item.count
          }))
        );

        setVisitsByDevice(
          data.visitsByDevice.map(item => ({
            name: item._id || 'Unknown',
            value: item.count
          }))
        );

        setVisitsByBrowser(
          data.visitsByBrowser.map(item => ({
            browser: item._id || 'Unknown',
            visits: item.count
          }))
        );

        setVisitsByReferrer(
          data.visitsByReferrer.map(item => ({
            source: item._id || 'Direct',
            visits: item.count
          }))
        );

        setVisitsOverTime(
          data.visitsOverTime.map(item => ({
            date: item.date,
            visits: item.visits,
            unique: item.uniqueVisitors
          }))
        );

        setLandingPages(
          data.landingPages.map(item => ({
            page: item._id || 'Unknown',
            visits: item.count
          }))
        );
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtimeVisitors = async () => {
    try {
      // Try both token keys (adminToken is the correct one)
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      
      // Silently skip if no token
      if (!token) {
        return;
      }
      
      const response = await fetch(
        `${API_URL}/api/analytics/visitors/realtime`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setRealtimeCount(result.count);
        }
      }
    } catch (err) {
      console.warn('Failed to fetch realtime visitors:', err);
    }
  };

  const fetchClickAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Not authenticated. Please login first.');
      }
      
      const dates = getDateRange(dateRange);
      
      const response = await fetch(
        `${API_URL}/api/analytics/clicks/summary?startDate=${dates.start}&endDate=${dates.end}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(`Failed to fetch click analytics (${response.status})`);
      }

      const result = await response.json();

      if (result.success) {
        const data = result.data;
        
        setClickStats({
          totalClicks: data.totalClicks || 0,
          uniqueSessions: data.uniqueSessions || 0,
          avgClicksPerSession: data.avgClicksPerSession || 0
        });

        // Format data for charts
        setClicksByElement(
          data.clicksByElement?.map(item => ({
            element: item._id || 'Unknown',
            clicks: item.count
          })) || []
        );

        setClicksByCategory(
          data.clicksByCategory?.map(item => ({
            name: item._id || 'Other',
            value: item.count
          })) || []
        );

        setClicksByPage(
          data.clicksByPage?.map(item => ({
            page: item._id || 'Unknown',
            clicks: item.count
          })) || []
        );

        setClicksOverTime(
          data.clicksOverTime?.map(item => ({
            date: item.date,
            clicks: item.clicks
          })) || []
        );
      }
    } catch (err) {
      console.error('Failed to fetch click analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>
            <FiBarChart2 />
            Visitor Analytics
          </Title>
        </Header>
        <LoadingState>
          <FiActivity size={48} />
          <p>Loading analytics data...</p>
        </LoadingState>
      </Container>
    );
  }

  if (error) {
    const isAuthError = error.includes('authenticated') || error.includes('login') || error.includes('expired');
    
    return (
      <Container>
        <Header>
          <Title>
            <FiBarChart2 />
            Visitor Analytics
          </Title>
        </Header>
        <ErrorState>
          <p>{error}</p>
          {isAuthError ? (
            <RefreshButton 
              onClick={() => window.location.href = '/login'} 
              style={{ marginTop: '1rem', background: '#ef4444' }}
            >
              Go to Login
            </RefreshButton>
          ) : (
            <RefreshButton onClick={fetchAnalytics} style={{ marginTop: '1rem' }}>
              <FiRefreshCw />
              Retry
            </RefreshButton>
          )}
        </ErrorState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <FiBarChart2 />
          Visitor Analytics
        </Title>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <RealtimeBadge>
            {realtimeCount} online now
          </RealtimeBadge>
          <RefreshButton onClick={fetchAnalytics} disabled={loading}>
            <FiRefreshCw />
            {loading ? 'Loading...' : 'Refresh'}
          </RefreshButton>
        </div>
      </Header>

      <TabContainer>
        <Tab active={activeTab === 'visitors'} onClick={() => setActiveTab('visitors')}>
          Visitors
        </Tab>
        <Tab active={activeTab === 'clicks'} onClick={() => setActiveTab('clicks')}>
          Clicks
        </Tab>
      </TabContainer>

      <DateFilter>
        <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
          <option value="24hours">Last 24 Hours</option>
          <option value="7days">Last 7 Days</option>
          <option value="30days">Last 30 Days</option>
          <option value="90days">Last 90 Days</option>
        </Select>
      </DateFilter>

      {activeTab === 'visitors' ? (
        <>
      <StatsGrid>
        <StatCard>
          <StatIcon color="#3b82f6">
            <FiUsers />
          </StatIcon>
          <StatLabel>Total Visits</StatLabel>
          <StatValue>{stats.totalVisits.toLocaleString()}</StatValue>
        </StatCard>

        <StatCard>
          <StatIcon color="#10b981">
            <FiUsers />
          </StatIcon>
          <StatLabel>Unique Visitors</StatLabel>
          <StatValue>{stats.uniqueVisitors.toLocaleString()}</StatValue>
        </StatCard>

        <StatCard>
          <StatIcon color="#f59e0b">
            <FiTrendingUp />
          </StatIcon>
          <StatLabel>Avg Visits/User</StatLabel>
          <StatValue>{stats.avgVisitsPerUser}</StatValue>
        </StatCard>

        <StatCard>
          <StatIcon color="#8b5cf6">
            <FiActivity />
          </StatIcon>
          <StatLabel>Real-time Visitors</StatLabel>
          <StatValue>{realtimeCount}</StatValue>
          <StatChange positive>Last 5 minutes</StatChange>
        </StatCard>
      </StatsGrid>

      <ChartSection>
        <ChartTitle>Visits Over Time</ChartTitle>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={visitsOverTime}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="visits" stroke="#3b82f6" strokeWidth={2} name="Total Visits" />
            <Line type="monotone" dataKey="unique" stroke="#10b981" strokeWidth={2} name="Unique Visitors" />
          </LineChart>
        </ResponsiveContainer>
      </ChartSection>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
        <ChartSection>
          <ChartTitle>Visits by Device</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={visitsByDevice}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {visitsByDevice.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartSection>

        <ChartSection>
          <ChartTitle>Traffic Sources</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={visitsByReferrer}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="source" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="visits" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </ChartSection>
      </div>

      <ChartSection>
        <ChartTitle>Most Visited Pages</ChartTitle>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={visitsByPage} layout="horizontal">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="page" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="visits" fill="#3b82f6" />
          </BarChart>
        </ResponsiveContainer>
      </ChartSection>

      <ChartSection>
        <ChartTitle>Top Landing Pages</ChartTitle>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={landingPages}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="page" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="visits" fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>
      </ChartSection>

      <ChartSection>
        <ChartTitle>Visits by Browser</ChartTitle>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={visitsByBrowser}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="browser" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="visits" fill="#8b5cf6" />
          </BarChart>
        </ResponsiveContainer>
      </ChartSection>
      </>
      ) : (
        <>
        {/* Click Analytics Dashboard */}
        <StatsGrid>
          <StatCard>
            <StatIcon color="#3b82f6">
              <FiActivity />
            </StatIcon>
            <StatLabel>Total Clicks</StatLabel>
            <StatValue>{clickStats.totalClicks.toLocaleString()}</StatValue>
          </StatCard>

          <StatCard>
            <StatIcon color="#10b981">
              <FiUsers />
            </StatIcon>
            <StatLabel>Unique Sessions</StatLabel>
            <StatValue>{clickStats.uniqueSessions.toLocaleString()}</StatValue>
          </StatCard>

          <StatCard>
            <StatIcon color="#f59e0b">
              <FiTrendingUp />
            </StatIcon>
            <StatLabel>Avg Clicks/Session</StatLabel>
            <StatValue>{clickStats.avgClicksPerSession}</StatValue>
          </StatCard>

          <StatCard>
            <StatIcon color="#8b5cf6">
              <FiBarChart2 />
            </StatIcon>
            <StatLabel>Tracked Elements</StatLabel>
            <StatValue>{clicksByElement.length}</StatValue>
            <StatChange positive>Active tracking</StatChange>
          </StatCard>
        </StatsGrid>

        <ChartSection>
          <ChartTitle>Clicks Over Time</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={clicksOverTime}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="clicks" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartSection>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <ChartSection>
            <ChartTitle>Click Distribution by Category</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={clicksByCategory}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {clicksByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartSection>

          <ChartSection>
            <ChartTitle>Clicks by Element</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={clicksByElement.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="element" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="clicks" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </ChartSection>
        </div>

        <ChartSection>
          <ChartTitle>Clicks by Page</ChartTitle>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={clicksByPage}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="page" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="clicks" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </ChartSection>
        </>
      )}
    </Container>
  );
};

export default Analytics;
